import { Link } from 'react-router';
/**
 * /admin/hubspot — HubSpot Integration admin console.
 *
 * Surfaces the integration_events + outbox_messages tables, a recent-events
 * feed with replay controls, and (mock-mode only) a "Simulate deal-won"
 * form that fires a synthetic webhook end-to-end so admins can exercise
 * the signature → persist → provision → outbox path without touching real
 * HubSpot.
 *
 * Capability model:
 *   - view:hubspot_integration   — required to land on this page
 *   - manage:hubspot_integration — required to drain outbox, replay events,
 *                                  and submit the simulate-deal-won form
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

const MOCK_MODE = import.meta.env.VITE_MOCK_MODE === '1';

type EventStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'dlq';
type OutboxStatus = 'pending' | 'sent' | 'failed' | 'dlq';

interface IntegrationEvent {
  id: string;
  source: string;
  externalEventId: string;
  subscriptionType: string;
  objectId: string | null;
  occurredAt: string;
  receivedAt: string;
  payload: unknown;
  status: string;
  attempts: number;
  lastError: string | null;
  processedAt: string | null;
  resultCustomerId: string | null;
  resultNotes: string | null;
}

interface EventsResponse {
  events: IntegrationEvent[];
  counts: Record<string, number>;
  nextCursor: string | null;
}

interface OutboxMessage {
  id: string;
  kind: string;
  payload: unknown;
  status: string;
  attempts: number;
  lastError: string | null;
  scheduledFor: string;
  sentAt: string | null;
  createdAt: string;
}

interface OutboxResponse {
  messages: OutboxMessage[];
  counts: Record<string, number>;
}

interface DrainResponse {
  ok: boolean;
  processed: number;
  sent: number;
  failed: number;
  dlq: number;
  dispatchedBy?: string;
}

interface ProvisionResult {
  customerId: string;
  customerNewlyCreated: boolean;
  contactsCreated: number;
  contactsUpdated: number;
  assignedAeEmail: string;
  assignedCsmEmail: string | null;
  assignedOpsMgrEmail: string | null;
  notes: string;
}

interface SimulateResponse {
  ok: boolean;
  eventId: number;
  dealId: string;
  companyName: string;
  signature: { ts: number; sig: string };
  provisionResult: ProvisionResult | null;
  provisionError: string | null;
  outboxResult: DrainResponse;
}

const EVENT_STATUSES: EventStatus[] = [
  'pending',
  'processing',
  'completed',
  'skipped',
  'failed',
  'dlq',
];

const OUTBOX_STATUSES: OutboxStatus[] = ['pending', 'sent', 'failed', 'dlq'];

export default function AdminHubspotPage() {
  const { meLoading, can } = useMeSafe();

  const [events, setEvents] = useState<IntegrationEvent[]>([]);
  const [eventCounts, setEventCounts] = useState<Record<string, number>>({});
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');

  const [outbox, setOutbox] = useState<OutboxMessage[]>([]);
  const [outboxCounts, setOutboxCounts] = useState<Record<string, number>>({});
  const [outboxError, setOutboxError] = useState<string | null>(null);

  const [drainToast, setDrainToast] = useState<DrainResponse | null>(null);

  // simulate-deal-won form state
  const [simCompany, setSimCompany] = useState('Acme Logistics Inc.');
  const [simIndustry, setSimIndustry] = useState('Technology');
  const [simEmployees, setSimEmployees] = useState('250');
  const [simContactName, setSimContactName] = useState('Alex Rivera');
  const [simContactEmail, setSimContactEmail] = useState('');
  const [simContactTitle, setSimContactTitle] = useState(
    'Director, Travel & Mobility',
  );
  const [simSubmitting, setSimSubmitting] = useState(false);
  const [simResult, setSimResult] = useState<SimulateResponse | null>(null);
  const [simError, setSimError] = useState<string | null>(null);

  const [replayBusy, setReplayBusy] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const qs = new URLSearchParams({ limit: '50' });
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      const data = await apiFetch<EventsResponse>(
        `/api/integrations/hubspot/events?${qs.toString()}`,
      );
      setEvents(data.events);
      setEventCounts(data.counts);
    } catch (e) {
      setEventsError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setEventsLoading(false);
    }
  }, [statusFilter]);

  const loadOutbox = useCallback(async () => {
    try {
      setOutboxError(null);
      const data = await apiFetch<OutboxResponse>(
        '/api/integrations/hubspot/outbox',
      );
      setOutbox(data.messages);
      setOutboxCounts(data.counts);
    } catch (e) {
      setOutboxError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (!meLoading && can('view:hubspot_integration')) {
      loadEvents();
      loadOutbox();
    }
  }, [meLoading, can, loadEvents, loadOutbox]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!drainToast) return;
    const t = setTimeout(() => setDrainToast(null), 4000);
    return () => clearTimeout(t);
  }, [drainToast]);

  // NOTE: every hook call MUST come before any early return below, or React
  // throws a "change in the order of Hooks" error on the next render.
  const totalEvents = useMemo(
    () => EVENT_STATUSES.reduce((acc, s) => acc + (eventCounts[s] ?? 0), 0),
    [eventCounts],
  );

  if (meLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Loading…</div>
      </div>
    );
  }

  if (!can('view:hubspot_integration')) {
    return (
      <div className="page">
        <div className="banner banner-error">
          You don&rsquo;t have permission to view the HubSpot Integration
          console. Ask an admin to grant the{' '}
          <code>view:hubspot_integration</code> capability.
        </div>
      </div>
    );
  }

  const canManage = can('manage:hubspot_integration');

  const submitSimulate = async () => {
    if (!canManage) return;
    try {
      setSimSubmitting(true);
      setSimError(null);
      setSimResult(null);
      const body: Record<string, unknown> = {
        companyName: simCompany,
        industry: simIndustry,
        employees: Number(simEmployees) || 0,
        primaryContactName: simContactName,
        primaryContactTitle: simContactTitle,
      };
      if (simContactEmail.trim())
        body.primaryContactEmail = simContactEmail.trim();
      const data = await apiFetch<SimulateResponse>(
        '/api/integrations/hubspot/simulate-deal-won',
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      );
      setSimResult(data);
      // Refresh feeds so the new event + outbox rows show up
      await Promise.all([loadEvents(), loadOutbox()]);
    } catch (e) {
      setSimError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSimSubmitting(false);
    }
  };

  const drainNow = async () => {
    if (!canManage) return;
    try {
      const data = await apiFetch<DrainResponse>(
        '/api/integrations/hubspot/outbox',
        {
          method: 'POST',
        },
      );
      setDrainToast(data);
      await loadOutbox();
    } catch (e) {
      setOutboxError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const replayEvent = async (externalEventId: string) => {
    if (!canManage) return;
    try {
      setReplayBusy(externalEventId);
      await apiFetch(
        `/api/integrations/hubspot/events/${encodeURIComponent(externalEventId)}/replay`,
        { method: 'POST' },
      );
      await Promise.all([loadEvents(), loadOutbox()]);
    } catch (e) {
      setEventsError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setReplayBusy(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>HubSpot Integration</h1>
        <p className="page-intro">
          Live view of inbound webhook events, the provisioning outbox, and the
          replay controls.{' '}
          {MOCK_MODE
            ? 'Mock mode is ON — the Simulate panel below fires a synthetic deal-won through the same code path as a real webhook.'
            : 'Mock mode is OFF — only real HubSpot traffic appears here.'}
        </p>
      </div>

      {/* Status strip */}
      <div className="kpi-row kpi-row-6">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Total persisted</span>
          <span className="kpi-value">{totalEvents}</span>
          <span className="kpi-sub">all statuses</span>
        </div>
        <StatusKpi
          label="Pending"
          value={eventCounts.pending ?? 0}
          tone="blue"
        />
        <StatusKpi
          label="Processing"
          value={eventCounts.processing ?? 0}
          tone="amber"
        />
        <StatusKpi
          label="Completed"
          value={eventCounts.completed ?? 0}
          tone="green"
        />
        <StatusKpi
          label="Skipped"
          value={eventCounts.skipped ?? 0}
          tone="grey"
        />
        <StatusKpi
          label="Failed / DLQ"
          value={(eventCounts.failed ?? 0) + (eventCounts.dlq ?? 0)}
          tone="red"
          emphasizeNonZero
          sub={`failed ${eventCounts.failed ?? 0} · dlq ${eventCounts.dlq ?? 0}`}
        />
      </div>

      {/* Simulate panel (mock only) */}
      {MOCK_MODE && (
        <div className="section">
          <div className="section-header">
            <span className="section-label">Simulate deal-won</span>
            <span
              style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(255, 200, 0, 0.18)',
                color: '#FFE08A',
                border: '1px solid rgba(255, 200, 0, 0.4)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              ⚠️ Mock mode only
            </span>
          </div>
          <div className="section-body">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <Field label="Company name">
                <input
                  className="input"
                  value={simCompany}
                  onChange={(e) => setSimCompany(e.target.value)}
                />
              </Field>
              <Field label="Industry">
                <input
                  className="input"
                  value={simIndustry}
                  onChange={(e) => setSimIndustry(e.target.value)}
                />
              </Field>
              <Field label="Employees">
                <input
                  className="input"
                  type="number"
                  value={simEmployees}
                  onChange={(e) => setSimEmployees(e.target.value)}
                />
              </Field>
              <Field label="Primary contact name">
                <input
                  className="input"
                  value={simContactName}
                  onChange={(e) => setSimContactName(e.target.value)}
                />
              </Field>
              <Field label="Primary contact email (optional)">
                <input
                  className="input"
                  type="email"
                  value={simContactEmail}
                  onChange={(e) => setSimContactEmail(e.target.value)}
                  placeholder="alex@acme-logistics.com"
                />
              </Field>
              <Field label="Primary contact title">
                <input
                  className="input"
                  value={simContactTitle}
                  onChange={(e) => setSimContactTitle(e.target.value)}
                />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                className="btn btn-primary"
                onClick={submitSimulate}
                disabled={!canManage || simSubmitting || !simCompany.trim()}
                title={
                  canManage ? undefined : 'manage:hubspot_integration required'
                }
              >
                {simSubmitting ? 'Firing…' : 'Fire synthetic deal-won'}
              </button>
              {!canManage && (
                <span
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Read-only — requires <code>manage:hubspot_integration</code>.
                </span>
              )}
            </div>

            {simError && (
              <div className="banner banner-error" style={{ marginTop: 12 }}>
                {simError}
              </div>
            )}

            {simResult && (
              <div
                style={{
                  marginTop: 14,
                  padding: 14,
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-sm)',
                  background:
                    'linear-gradient(180deg, var(--accent-light) 0%, hsl(var(--surface-background)) 100%)',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Event fired: #{simResult.eventId} (deal {simResult.dealId} ·{' '}
                  {simResult.companyName})
                </div>
                {simResult.provisionError ? (
                  <div
                    style={{ color: 'var(--red)', fontSize: 'var(--fs-base)' }}
                  >
                    Provision error: {simResult.provisionError}
                  </div>
                ) : simResult.provisionResult ? (
                  <ResultBlock result={simResult.provisionResult} />
                ) : (
                  <div
                    style={{
                      fontSize: 'var(--fs-base)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    No provision result returned.
                  </div>
                )}
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Outbox drain: processed {simResult.outboxResult.processed} ·
                  sent {simResult.outboxResult.sent} · failed{' '}
                  {simResult.outboxResult.failed} · dlq{' '}
                  {simResult.outboxResult.dlq}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Events table */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Recent events</span>
          <button
            className="btn btn-outline btn-sm"
            style={{
              background: 'rgba(255,254,252,0.06)',
              borderColor: 'rgba(255,254,252,0.18)',
              color: 'var(--text-white)',
            }}
            onClick={() => loadEvents()}
          >
            Refresh
          </button>
        </div>
        <div className="section-body">
          {eventsError && (
            <div className="banner banner-error">{eventsError}</div>
          )}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 12,
            }}
          >
            <FilterChip
              label="All"
              active={statusFilter === 'all'}
              count={totalEvents}
              onClick={() => setStatusFilter('all')}
            />
            {EVENT_STATUSES.map((s) => (
              <FilterChip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                active={statusFilter === s}
                count={eventCounts[s] ?? 0}
                onClick={() => setStatusFilter(s)}
                tone={statusTone(s)}
              />
            ))}
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Subscription type</th>
                  <th>Object id</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Attempts</th>
                  <th>Customer</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {eventsLoading && events.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>Loading…</td>
                  </tr>
                )}
                {!eventsLoading && events.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>No events for this filter yet.</td>
                  </tr>
                )}
                {events.map((ev) => {
                  const replayable =
                    ev.status === 'failed' || ev.status === 'dlq';
                  return (
                    <tr key={ev.id}>
                      <td
                        style={{
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtRelative(ev.receivedAt)}
                      </td>
                      <td
                        style={{
                          fontFamily: 'SF Mono, Menlo, monospace',
                          fontSize: 'var(--fs-sm)',
                        }}
                      >
                        {ev.subscriptionType}
                      </td>
                      <td
                        style={{
                          fontFamily: 'SF Mono, Menlo, monospace',
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {ev.objectId ?? '—'}
                      </td>
                      <td>
                        <StatusChip status={ev.status} />
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {ev.attempts}
                      </td>
                      <td
                        style={{
                          fontFamily: 'SF Mono, Menlo, monospace',
                          fontSize: 'var(--fs-sm)',
                        }}
                      >
                        {ev.resultCustomerId ? (
                          <Link
                            to={`/crm/customers/${encodeURIComponent(ev.resultCustomerId)}`}
                            style={{ color: 'var(--accent-darker)' }}
                          >
                            {ev.resultCustomerId}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td
                        style={{
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-muted)',
                          maxWidth: 360,
                        }}
                        title={ev.resultNotes ?? ev.lastError ?? ''}
                      >
                        {truncate(ev.lastError ?? ev.resultNotes ?? '', 120)}
                      </td>
                      <td>
                        {replayable && canManage && (
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => replayEvent(ev.externalEventId)}
                            disabled={replayBusy === ev.externalEventId}
                          >
                            {replayBusy === ev.externalEventId
                              ? 'Replaying…'
                              : 'Replay'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Outbox panel */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Outbox</span>
          <button
            className="btn btn-outline btn-sm"
            style={{
              background: 'rgba(255,254,252,0.06)',
              borderColor: 'rgba(255,254,252,0.18)',
              color: 'var(--text-white)',
            }}
            onClick={() => drainNow()}
            disabled={!canManage}
            title={
              canManage ? undefined : 'manage:hubspot_integration required'
            }
          >
            Drain pending now
          </button>
        </div>
        <div className="section-body">
          {outboxError && (
            <div className="banner banner-error">{outboxError}</div>
          )}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 12,
            }}
          >
            {OUTBOX_STATUSES.map((s) => (
              <FilterChip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                active={false}
                count={outboxCounts[s] ?? 0}
                onClick={() => {
                  /* outbox isn't filterable from chip-only — chips are informational */
                }}
                tone={outboxTone(s)}
                inert
              />
            ))}
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Attempts</th>
                  <th>Last error</th>
                  <th>Scheduled for</th>
                  <th>Sent at</th>
                </tr>
              </thead>
              <tbody>
                {outbox.length === 0 && (
                  <tr className="empty">
                    <td colSpan={6}>Outbox is empty.</td>
                  </tr>
                )}
                {outbox.map((m) => (
                  <tr key={m.id}>
                    <td
                      style={{
                        fontFamily: 'SF Mono, Menlo, monospace',
                        fontSize: 'var(--fs-sm)',
                      }}
                    >
                      {m.kind}
                    </td>
                    <td>
                      <StatusChip status={m.status} />
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {m.attempts}
                    </td>
                    <td
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-muted)',
                        maxWidth: 320,
                      }}
                      title={m.lastError ?? ''}
                    >
                      {truncate(m.lastError ?? '', 120) || '—'}
                    </td>
                    <td
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtRelative(m.scheduledFor)}
                    </td>
                    <td
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m.sentAt ? fmtRelative(m.sentAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drain toast */}
      {drainToast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '12px 16px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-card)',
            border: '1px solid var(--accent-soft)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            fontSize: 'var(--fs-base)',
            color: 'var(--text-primary)',
            zIndex: 1000,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Outbox drained</div>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            processed {drainToast.processed} · sent {drainToast.sent} · failed{' '}
            {drainToast.failed} · dlq {drainToast.dlq}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── helpers + small components ──────────────────────────────────────── */

/**
 * Wrapper around useMe() that surfaces a stable `meLoading` boolean. The
 * underlying hook returns `loading` — alias here so the component reads
 * cleanly.
 */
function useMeSafe() {
  const { loading, can } = useMe();
  return { meLoading: loading, can };
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="field-label"
        style={{ display: 'block', marginBottom: 4 }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ResultBlock({ result }: { result: ProvisionResult }) {
  return (
    <div style={{ fontSize: 'var(--fs-base)', lineHeight: 1.6 }}>
      <div>
        Customer:{' '}
        {result.customerId ? (
          <Link
            to={`/crm/customers/${encodeURIComponent(result.customerId)}`}
            style={{
              color: 'var(--accent-darker)',
              fontFamily: 'SF Mono, Menlo, monospace',
            }}
          >
            {result.customerId}
          </Link>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>
            (skipped — no customer provisioned)
          </span>
        )}{' '}
        {result.customerNewlyCreated && (
          <em style={{ color: 'var(--text-muted)' }}>(new)</em>
        )}
      </div>
      <div>
        Contacts: {result.contactsCreated} created, {result.contactsUpdated}{' '}
        updated
      </div>
      <div>
        Routing — AE: <strong>{result.assignedAeEmail || '—'}</strong>
        {result.assignedCsmEmail ? (
          <>
            {' '}
            · CSM: <strong>{result.assignedCsmEmail}</strong>
          </>
        ) : null}
        {result.assignedOpsMgrEmail ? (
          <>
            {' '}
            · Ops: <strong>{result.assignedOpsMgrEmail}</strong>
          </>
        ) : null}
      </div>
      <div
        style={{
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
          marginTop: 4,
        }}
      >
        {result.notes}
      </div>
    </div>
  );
}

type Tone = 'blue' | 'amber' | 'green' | 'grey' | 'red' | 'red-dark';

function statusTone(s: string): Tone {
  switch (s) {
    case 'pending':
      return 'blue';
    case 'processing':
      return 'amber';
    case 'completed':
    case 'sent':
      return 'green';
    case 'skipped':
      return 'grey';
    case 'failed':
      return 'red';
    case 'dlq':
      return 'red-dark';
    default:
      return 'grey';
  }
}

function outboxTone(s: string): Tone {
  return statusTone(s);
}

const TONE_STYLES: Record<Tone, { bg: string; color: string; border: string }> =
  {
    blue: {
      bg: 'rgba(14,125,154,0.16)',
      color: '#0E7D9A',
      border: 'rgba(14,125,154,0.4)',
    },
    amber: {
      bg: 'rgba(214,150,38,0.18)',
      color: '#8A5A11',
      border: 'rgba(214,150,38,0.45)',
    },
    green: {
      bg: 'rgba(43,138,62,0.16)',
      color: '#256E33',
      border: 'rgba(43,138,62,0.4)',
    },
    grey: {
      bg: 'var(--bg-subtle)',
      color: 'var(--text-muted)',
      border: 'var(--border-light)',
    },
    red: {
      bg: 'rgba(196,55,55,0.16)',
      color: '#9C2F2F',
      border: 'rgba(196,55,55,0.45)',
    },
    'red-dark': {
      bg: 'rgba(140,30,30,0.22)',
      color: '#6E1F1F',
      border: 'rgba(140,30,30,0.7)',
    },
  };

function StatusChip({ status }: { status: string }) {
  const tone = statusTone(status);
  const t = TONE_STYLES[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 'var(--radius-pill)',
        background: t.bg,
        color: t.color,
        border:
          tone === 'red-dark'
            ? `2px solid ${t.border}`
            : `1px solid ${t.border}`,
        fontSize: 'var(--fs-xs)',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
  tone,
  inert,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: Tone;
  inert?: boolean;
}) {
  const t = tone ? TONE_STYLES[tone] : null;
  return (
    <button
      onClick={onClick}
      disabled={inert}
      style={{
        cursor: inert ? 'default' : 'pointer',
        fontFamily: 'inherit',
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: active
          ? 'var(--accent-light)'
          : t
            ? t.bg
            : 'var(--bg-card)',
        color: active
          ? 'var(--accent-darker)'
          : t
            ? t.color
            : 'var(--text-muted)',
        border: active
          ? '1px solid var(--accent)'
          : t
            ? `1px solid ${t.border}`
            : '1px solid var(--border)',
        fontSize: 'var(--fs-xs)',
        fontWeight: active ? 700 : 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        opacity: inert ? 0.85 : 1,
      }}
    >
      {label}
      <span
        style={{
          marginLeft: 6,
          fontVariantNumeric: 'tabular-nums',
          opacity: 0.8,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function StatusKpi({
  label,
  value,
  tone,
  emphasizeNonZero,
  sub,
}: {
  label: string;
  value: number;
  tone: Tone;
  emphasizeNonZero?: boolean;
  sub?: string;
}) {
  const t = TONE_STYLES[tone];
  const hot = emphasizeNonZero && value > 0;
  return (
    <div
      className="kpi-card"
      style={
        hot
          ? {
              borderColor: t.border,
              background: `linear-gradient(180deg, ${t.bg} 0%, var(--bg-card) 100%)`,
            }
          : undefined
      }
    >
      <span className="kpi-label">{label}</span>
      <span
        className="kpi-value kpi-value-sm"
        style={hot ? { color: t.color } : undefined}
      >
        {value}
      </span>
      <span className="kpi-sub">
        {sub ?? (hot ? 'needs attention' : 'events')}
      </span>
    </div>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec} seconds ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return min === 1 ? '1 minute ago' : `${min} minutes ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

function truncate(s: string, max: number): string {
  if (!s) return '';
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}
